import { type MarketDataSearchSource, type Prisma } from "@prisma/client";
import { nanoid } from "nanoid/async";
import { type AppContext } from "~/lib/context";
import { getRequiredUser } from "~/lib/getRequiredUser";
import { type Input as GetMarketDataStatsInput, type MarketOrCompareInput } from "~/pages/api/get-market-data-stats";
import { splitCustomJobIds } from "~/services/custom-jobs/splitCustomJobIds";
import { getHeadcountRange, mapRangeToHeadcount } from "~/services/headcount";
import { DEFAULT_MARKET_DATA_MEASURE } from "~/services/market-data/market-data-filters/parseQueryString";

export const createMarketDataSearchFromInput = async (
  ctx: AppContext,
  input: {
    source: MarketDataSearchSource;
    search: GetMarketDataStatsInput;
  }
) => {
  const user = getRequiredUser(ctx);
  const canAccessFiguresAiV2 = ctx.featureFlags.CAN_ACCESS_FIGURES_AI_V2;

  const splitJobs = splitCustomJobIds(input.search.market.jobsIds);
  const comparedSplitJobs = splitCustomJobIds(input.search.compare?.jobsIds ?? []);

  return ctx.prisma.marketDataSearch.create({
    data: {
      user: { connect: { id: user.id } },
      source: input.source,
      hash: await nanoid(),
      input: {
        useTestData: input.search.useTestData,
        measure: input.search.measure,
        currency: input.search.currency,
        levels: input.search.levels,
        benchmarkLevelIds: ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS ? input.search.benchmarkLevelIds : [],
        percentiles: input.search.percentiles,
        mergeAdvancedLevels: input.search.mergeAdvancedLevels,
        showCompanyEmployees: input.search.showCompanyEmployees,
      },
      jobs: { connect: splitJobs.jobIds.map((jobId) => ({ id: jobId })) },
      customJobs: { connect: splitJobs.customJobIds.map((jobId) => ({ id: jobId })) },
      comparedJobs: { connect: comparedSplitJobs.jobIds.map((jobId) => ({ id: jobId })) },
      comparedCustomJobs: { connect: comparedSplitJobs.customJobIds.map((jobId) => ({ id: jobId })) },
      marketFilters: {
        create: {
          ...getHeadcountPayload(input.search.market, { canAccessFiguresAiV2 }),
          ...getIndustryPayload(input.search.market, { canAccessFiguresAiV2 }),
          fundingRounds: input.search.market.fundingRounds,
          labels: { connect: input.search.market.labelsIds.map((id) => ({ id })) },
          locations: { connect: input.search.market.locationsIds.map((id) => ({ id })) },
        },
      },
      ...(input.search.compare && {
        comparedMarketFilters: {
          create: {
            ...getHeadcountPayload(input.search.compare, { canAccessFiguresAiV2 }),
            ...getIndustryPayload(input.search.compare, { canAccessFiguresAiV2 }),
            fundingRounds: input.search.compare.fundingRounds,
            labels: { connect: input.search.compare.labelsIds.map((id) => ({ id })) },
            locations: { connect: input.search.compare.locationsIds.map((id) => ({ id })) },
          },
        },
      }),
    },
  });
};

export const getHeadcountPayload = (
  input: Pick<MarketOrCompareInput, "headcount" | "size">,
  options: { canAccessFiguresAiV2: boolean }
) => {
  if (options.canAccessFiguresAiV2) {
    const { min, max } = getHeadcountRange(input.headcount);

    return { headcount: input.headcount, minEmployeesCount: min, maxEmployeesCount: max };
  }

  return {
    headcount: mapRangeToHeadcount(input.size),
    minEmployeesCount: input.size.min,
    maxEmployeesCount: input.size.max,
  };
};

export const getIndustryPayload = (
  input: Pick<MarketOrCompareInput, "industryId" | "industriesIds">,
  options: { canAccessFiguresAiV2: boolean }
) => {
  if (options.canAccessFiguresAiV2 && input.industryId) {
    return {
      ...(input.industryId && {
        industries: { connect: [{ id: input.industryId }] },
        industry: { connect: { id: input.industryId } },
      }),
    };
  }

  const industryId = input.industriesIds[0];
  if (industryId) {
    return {
      industries: { connect: input.industriesIds.map((id) => ({ id })) },
      industry: { connect: { id: industryId } },
    };
  }

  return {};
};

export const fetchMarketDataSearch = async (ctx: AppContext, hash: string) => {
  const marketDataSearch = await ctx.prisma.marketDataSearch.findUniqueOrThrow({
    where: { hash },
    include: {
      user: {
        include: {
          company: true,
        },
      },
      marketFilters: {
        include: {
          labels: true,
          industries: true,
          industry: true,
          locations: {
            include: { country: true },
          },
        },
      },
      jobs: {
        select: {
          id: true,
          name: true,
          description: true,
          descriptionTranslations: true,
          familyId: true,
          family: {
            select: {
              id: true,
              name: true,
            },
          },
        },
      },
      customJobs: true,
      comparedMarketFilters: {
        include: {
          labels: true,
          industries: true,
          industry: true,
          locations: {
            include: { country: true },
          },
        },
      },
      comparedJobs: {
        select: {
          id: true,
          name: true,
          description: true,
          descriptionTranslations: true,
          familyId: true,
          family: {
            select: {
              id: true,
              name: true,
            },
          },
        },
      },
      comparedCustomJobs: true,
    },
  });

  return {
    ...marketDataSearch,
    input: backfillInput(marketDataSearch.input as Prisma.JsonObject),
  };
};

const backfillInput = (input: Prisma.JsonObject) => {
  // the following attributes have been added later on and might not be present in the input stored in DB
  if (!input.measure) {
    input.measure = DEFAULT_MARKET_DATA_MEASURE;
  }
  if (!input.showCompanyEmployees) {
    input.showCompanyEmployees = false;
  }

  return input;
};
